/**
 * BulkContentPlanner
 *
 * Main modal component for bulk content planning workflow.
 * Guides users through: Overview → Generate Briefs → Review → Generate Creatives → A/B Tests → Smart Schedule
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    X,
    Calendar,
    Sparkles,
    CheckCircle,
    ArrowRight,
    ArrowLeft,
    Loader2,
    FileText,
    FlaskConical,
    CalendarCheck,
    AlertCircle,
    Image,
    Wand2,
    Play,
    Layers
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../contexts/AppContext';
import { useAuth } from '../../contexts/AuthContext';
import { getPillarsByBrand } from '../../services/pillarService';
import { getStrategyDashboardData } from '../../services/strategyService';
import {
    calculateRecommendedVolume,
    createContentPlan,
    createContentBriefsBatch,
    updateContentBrief,
    DEFAULT_CONTENT_PLAN,
    scheduleContentBriefs,
    getContentPlanWithBriefs
} from '../../services/contentPlanService';
import { generateBulkBriefs, suggestABTests, generateABVariant } from '../../services/bulkBriefService';
import { getPromotionsByBrand } from '../../services/promotionService';
import { getProductsByBrand } from '../../services/productService';
import { getOrganicPosts } from '../../services/calendarService';
import PlanOverview from './PlanOverview';
import ProductAssignmentStep from './ProductAssignmentStep';
import BriefGrid from './BriefGrid';
import BriefDetailModal from './BriefDetailModal';
import ABTestSuggestions from './ABTestSuggestions';
import BulkGenerationStep from './BulkGenerationStep';
import SmartScheduleStep from './SmartScheduleStep';
import DeployToFunnelModal from './DeployToFunnelModal';

const STEPS = [
    { id: 'overview', label: 'Plan Overview', icon: Calendar, description: 'Configure volume and distribution' },
    { id: 'products', label: 'Assign Products', icon: Layers, description: 'Link products to briefs' },
    { id: 'generate', label: 'Generate Briefs', icon: Sparkles, description: 'AI generates content briefs' },
    { id: 'review', label: 'Review & Refine', icon: FileText, description: 'Review and edit briefs' },
    { id: 'creatives', label: 'Generate Creatives', icon: Play, description: 'Generate images/videos' },
    { id: 'testing', label: 'A/B Testing', icon: FlaskConical, description: 'Configure tests' },
    { id: 'schedule', label: 'Smart Schedule', icon: CalendarCheck, description: 'Auto-schedule posts' }
];

const BulkContentPlanner = ({ isOpen, onClose, onComplete, resumePlanId = null }) => {
    const { selectedBrand } = useApp();
    const { currentUser } = useAuth();
    const navigate = useNavigate();

    // Step management
    const [currentStep, setCurrentStep] = useState(0);
    const [completedSteps, setCompletedSteps] = useState([]);

    // Data states
    const [pillars, setPillars] = useState([]);
    const [businessGoals, setBusinessGoals] = useState({});
    const [plan, setPlan] = useState(null);
    const planIdRef = useRef(null);
    const [briefs, setBriefs] = useState([]);
    const [abTestSuggestions, setAbTestSuggestions] = useState([]);
    const [selectedEvents, setSelectedEvents] = useState([]);
    const [promotions, setPromotions] = useState([]);
    const [selectedPromotions, setSelectedPromotions] = useState([]);
    const [products, setProducts] = useState([]);
    const [productAssignments, setProductAssignments] = useState([]); // Array of { pillarId, funnelStage, productId }

    // UI states
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [generationProgress, setGenerationProgress] = useState(null);
    const [error, setError] = useState(null);
    const [isGenerationActive, setIsGenerationActive] = useState(false); // Prevents closing during generation

    // A/B Test configuration
    const [configuringTest, setConfiguringTest] = useState(null);
    const [testConfigProgress, setTestConfigProgress] = useState(null);

    // Brief detail modal
    const [selectedBrief, setSelectedBrief] = useState(null);
    const [showBriefDetail, setShowBriefDetail] = useState(false);

    // Deploy to Funnel
    const [scheduledPosts, setScheduledPosts] = useState([]);
    const [showDeployToFunnel, setShowDeployToFunnel] = useState(false);

    // Load initial data or resume existing plan
    useEffect(() => {
        if (isOpen && selectedBrand?.id) {
            if (resumePlanId) {
                loadExistingPlan(resumePlanId);
            } else {
                loadInitialData();
            }
        }
    }, [isOpen, selectedBrand?.id, resumePlanId]);

    const loadInitialData = async () => {
        setLoading(true);
        setError(null);

        try {
            const [pillarsData, strategyData, promotionsData, productsData] = await Promise.all([
                getPillarsByBrand(selectedBrand.id),
                getStrategyDashboardData(selectedBrand.id),
                getPromotionsByBrand(selectedBrand.id),
                getProductsByBrand(selectedBrand.id)
            ]);

            setPromotions(promotionsData || []);
            setProducts(productsData || []);

            setPillars(pillarsData || []);
            setBusinessGoals(strategyData?.businessGoals || {});

            // Smart date selection: look at existing scheduled posts to avoid overlap
            const now = new Date();
            const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
            let startDate = tomorrow;

            try {
                // Query scheduled posts for the next 3 months
                const lookAheadEnd = new Date(now.getFullYear(), now.getMonth() + 3, 0);
                const existingPosts = await getOrganicPosts(selectedBrand.id, tomorrow, lookAheadEnd);

                if (existingPosts.length > 0) {
                    // Collect all scheduled dates
                    const scheduledDates = [];
                    for (const post of existingPosts) {
                        const dateStr = post.scheduledDate?.slice(0, 10);
                        if (dateStr && !scheduledDates.includes(dateStr)) {
                            scheduledDates.push(dateStr);
                        }
                    }
                    scheduledDates.sort();

                    if (scheduledDates.length >= 2) {
                        // Find the main cluster of dates by removing outliers
                        // An outlier is a date that is far from any other scheduled date (>7 day gap on both sides)
                        const nonOutlierDates = scheduledDates.filter((dateStr, i) => {
                            const curr = new Date(dateStr + 'T00:00:00').getTime();
                            const msPerDay = 86400000;

                            const prevGap = i > 0
                                ? (curr - new Date(scheduledDates[i - 1] + 'T00:00:00').getTime()) / msPerDay
                                : Infinity;
                            const nextGap = i < scheduledDates.length - 1
                                ? (new Date(scheduledDates[i + 1] + 'T00:00:00').getTime() - curr) / msPerDay
                                : Infinity;

                            // Keep if close to at least one neighbor (within 7 days)
                            return prevGap <= 7 || nextGap <= 7;
                        });

                        const datesToUse = nonOutlierDates.length > 0 ? nonOutlierDates : scheduledDates;
                        const lastDateStr = datesToUse[datesToUse.length - 1];
                        const lastScheduledDate = new Date(lastDateStr + 'T00:00:00');
                        const dayAfterLast = new Date(lastScheduledDate);
                        dayAfterLast.setDate(dayAfterLast.getDate() + 1);

                        if (dayAfterLast > tomorrow) {
                            startDate = dayAfterLast;
                        }
                    } else if (scheduledDates.length === 1) {
                        // Single scheduled date — treat as a 1-off, don't shift start date
                    }
                }
            } catch (err) {
                console.warn('Could not fetch existing posts for smart date selection:', err.message);
                // Fall back to tomorrow — non-critical
            }

            // End date: ~30 days from start date, or end of that month (whichever is later)
            const endOfStartMonth = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);
            const thirtyDaysOut = new Date(startDate.getTime() + 30 * 24 * 60 * 60 * 1000);
            const endDate = thirtyDaysOut > endOfStartMonth ? thirtyDaysOut : endOfStartMonth;
            const daysInRange = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

            // Calculate initial recommendations
            const recommendations = await calculateRecommendedVolume(
                selectedBrand.id,
                {
                    includeOrganic: true,
                    includePaid: true,
                    targetPlatforms: ['instagram', 'facebook'],
                    volumeOverride: null,
                    daysInRange
                },
                pillarsData,
                strategyData?.businessGoals
            );

            // Format date in local timezone (not UTC)
            const formatDate = (date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            // Create initial plan object with date range
            // Store base counts so we can restore them when toggling organic/paid
            const initialPlan = {
                ...DEFAULT_CONTENT_PLAN,
                name: `Content Plan`,
                startDate: formatDate(startDate),
                endDate: formatDate(endDate),
                config: {
                    includeOrganic: true,
                    includePaid: true,
                    targetPlatforms: ['instagram', 'facebook'],
                    volumeOverride: null,
                    daysInRange,
                    contentTypeDistribution: { image: 50, video: 30, carousel: 20 },
                    // Store base counts for redistribution when toggling
                    baseOrganicPosts: recommendations.organicPosts,
                    basePaidCreatives: recommendations.paidCreatives
                },
                recommendations
            };

            setPlan(initialPlan);
        } catch (err) {
            console.error('Error loading initial data:', err);
            setError('Failed to load strategy data');
        } finally {
            setLoading(false);
        }
    };

    // Load an existing plan and its briefs (for resuming incomplete plans)
    const loadExistingPlan = async (planId) => {
        setLoading(true);
        setError(null);

        try {
            // Load all necessary data in parallel
            const [planData, pillarsData, strategyData, promotionsData, productsData] = await Promise.all([
                getContentPlanWithBriefs(planId, currentUser?.uid),
                getPillarsByBrand(selectedBrand.id),
                getStrategyDashboardData(selectedBrand.id),
                getPromotionsByBrand(selectedBrand.id),
                getProductsByBrand(selectedBrand.id)
            ]);

            if (!planData) {
                setError('Plan not found');
                setLoading(false);
                return;
            }

            // Set all the data
            setPlan(planData.plan);
            planIdRef.current = planData.plan?.id || planId;
            setBriefs(planData.briefs || []);
            setPillars(pillarsData || []);
            setBusinessGoals(strategyData?.businessGoals || {});
            setPromotions(promotionsData || []);
            setProducts(productsData || []);

            // Restore selected events and promotions from plan
            if (planData.plan?.selectedEvents) {
                setSelectedEvents(planData.plan.selectedEvents);
            }
            if (planData.plan?.selectedPromotions) {
                setSelectedPromotions(planData.plan.selectedPromotions);
            }

            // Detect the correct step based on brief statuses
            const loadedBriefs = planData.briefs || [];
            const { step, completedSteps: completed } = detectPlanStep(loadedBriefs, planData.plan);

            setCompletedSteps(completed);
            setCurrentStep(step);

            // Generate A/B test suggestions if we're past the creatives step
            if (step >= 5 && loadedBriefs.length > 0) {
                suggestABTests(loadedBriefs).then(suggestions => {
                    setAbTestSuggestions(suggestions?.suggestions || []);
                });
            }
        } catch (err) {
            console.error('Error loading existing plan:', err);
            setError('Failed to load plan data');
        } finally {
            setLoading(false);
        }
    };

    // Detect which step the plan should resume at based on brief statuses
    const detectPlanStep = (planBriefs, plan) => {
        if (!planBriefs || planBriefs.length === 0) {
            // No briefs generated yet - start at overview or generate step
            if (plan?.id) {
                // Plan exists but no briefs - go to generate step
                return { step: 1, completedSteps: ['overview'] };
            }
            return { step: 0, completedSteps: [] };
        }

        const hasApproved = planBriefs.some(b => b.status === 'approved' || b.status === 'generated' || b.status === 'scheduled');
        const hasGenerated = planBriefs.some(b => b.status === 'generated' || b.status === 'scheduled');
        const allScheduled = planBriefs.every(b => b.status === 'scheduled');
        const hasScheduled = planBriefs.some(b => b.status === 'scheduled');

        if (allScheduled) {
            // All done - show schedule step as completed
            return {
                step: 6,
                completedSteps: ['overview', 'generate', 'review', 'creatives', 'testing', 'schedule']
            };
        }

        if (hasScheduled || hasGenerated) {
            // Some creatives generated - go to A/B testing step
            return {
                step: 5,
                completedSteps: ['overview', 'generate', 'review', 'creatives']
            };
        }

        if (hasApproved) {
            // Briefs approved but not generated - go to creatives step
            return {
                step: 4,
                completedSteps: ['overview', 'generate', 'review']
            };
        }

        // Briefs exist but not approved - go to review step
        return {
            step: 2,
            completedSteps: ['overview', 'generate']
        };
    };

    // Handle plan configuration changes (primarily for platform changes)
    const handlePlanUpdate = useCallback(async (updates) => {
        if (!plan) return;

        const newConfig = { ...plan.config, ...updates };

        // Recalculate recommendations if config changed
        const newRecommendations = await calculateRecommendedVolume(
            selectedBrand.id,
            newConfig,
            pillars,
            businessGoals
        );

        // Update base counts if platforms changed (this affects volume calculations)
        const updatedConfig = {
            ...newConfig,
            baseOrganicPosts: newRecommendations.organicPosts,
            basePaidCreatives: newRecommendations.paidCreatives
        };

        setPlan(prev => ({
            ...prev,
            config: updatedConfig,
            recommendations: newRecommendations
        }));
    }, [plan, selectedBrand?.id, pillars, businessGoals]);

    // Handle funnel distribution changes
    const handleFunnelDistributionChange = useCallback((distribution) => {
        if (!plan) return;

        const total = plan.recommendations.totalPosts;
        const newByFunnelStage = {
            TOFU: { count: Math.ceil(total * (distribution.TOFU / 100)), percentage: distribution.TOFU },
            MOFU: { count: Math.ceil(total * (distribution.MOFU / 100)), percentage: distribution.MOFU },
            BOFU: { count: total - Math.ceil(total * (distribution.TOFU / 100)) - Math.ceil(total * (distribution.MOFU / 100)), percentage: distribution.BOFU }
        };

        setPlan(prev => ({
            ...prev,
            recommendations: {
                ...prev.recommendations,
                byFunnelStage: newByFunnelStage
            }
        }));
    }, [plan]);

    // Handle content type distribution changes
    const handleContentTypeDistributionChange = useCallback((distribution) => {
        if (!plan) return;

        const total = plan.recommendations.totalPosts;
        const newByContentType = {
            image: Math.ceil(total * (distribution.image / 100)),
            video: Math.ceil(total * (distribution.video / 100)),
            carousel: Math.ceil(total * (distribution.carousel / 100))
        };

        setPlan(prev => ({
            ...prev,
            config: {
                ...prev.config,
                contentTypeDistribution: distribution
            },
            recommendations: {
                ...prev.recommendations,
                byContentType: newByContentType
            }
        }));
    }, [plan]);

    // Handle organic/paid toggle while maintaining total posts
    const handleContentTypeToggle = useCallback((type) => {
        if (!plan) return;

        const { config, recommendations } = plan;
        const { baseOrganicPosts = recommendations.organicPosts, basePaidCreatives = recommendations.paidCreatives } = config;
        const currentTotal = recommendations.totalPosts;

        let newIncludeOrganic = config.includeOrganic;
        let newIncludePaid = config.includePaid;
        let newOrganicPosts = recommendations.organicPosts;
        let newPaidCreatives = recommendations.paidCreatives;

        if (type === 'organic') {
            newIncludeOrganic = !config.includeOrganic;
            if (!newIncludeOrganic) {
                // Turning off organic - give all to paid (if paid is on)
                if (config.includePaid) {
                    newOrganicPosts = 0;
                    newPaidCreatives = currentTotal;
                } else {
                    // Can't turn off both - keep organic on
                    return;
                }
            } else {
                // Turning on organic - restore from base counts
                const baseTotal = baseOrganicPosts + basePaidCreatives;
                if (config.includePaid) {
                    // Both on - restore original proportions scaled to current total
                    const organicRatio = baseTotal > 0 ? baseOrganicPosts / baseTotal : 0.7;
                    newOrganicPosts = Math.round(currentTotal * organicRatio);
                    newPaidCreatives = currentTotal - newOrganicPosts;
                } else {
                    // Only organic - takes all
                    newOrganicPosts = currentTotal;
                    newPaidCreatives = 0;
                }
            }
        } else if (type === 'paid') {
            newIncludePaid = !config.includePaid;
            if (!newIncludePaid) {
                // Turning off paid - give all to organic (if organic is on)
                if (config.includeOrganic) {
                    newPaidCreatives = 0;
                    newOrganicPosts = currentTotal;
                } else {
                    // Can't turn off both - keep paid on
                    return;
                }
            } else {
                // Turning on paid - restore from base counts
                const baseTotal = baseOrganicPosts + basePaidCreatives;
                if (config.includeOrganic) {
                    // Both on - restore original proportions scaled to current total
                    const paidRatio = baseTotal > 0 ? basePaidCreatives / baseTotal : 0.3;
                    newPaidCreatives = Math.round(currentTotal * paidRatio);
                    newOrganicPosts = currentTotal - newPaidCreatives;
                } else {
                    // Only paid - takes all
                    newPaidCreatives = currentTotal;
                    newOrganicPosts = 0;
                }
            }
        }

        // Recalculate funnel and content type distributions based on new total
        const newTotal = newOrganicPosts + newPaidCreatives;
        const { byFunnelStage, byContentType } = recommendations;
        const contentTypeDist = config.contentTypeDistribution || { image: 50, video: 30, carousel: 20 };

        const newByFunnelStage = {
            TOFU: { count: Math.ceil(newTotal * (byFunnelStage.TOFU.percentage / 100)), percentage: byFunnelStage.TOFU.percentage },
            MOFU: { count: Math.ceil(newTotal * (byFunnelStage.MOFU.percentage / 100)), percentage: byFunnelStage.MOFU.percentage },
            BOFU: { count: newTotal - Math.ceil(newTotal * (byFunnelStage.TOFU.percentage / 100)) - Math.ceil(newTotal * (byFunnelStage.MOFU.percentage / 100)), percentage: byFunnelStage.BOFU.percentage }
        };

        const newByContentType = {
            image: Math.ceil(newTotal * (contentTypeDist.image / 100)),
            video: Math.ceil(newTotal * (contentTypeDist.video / 100)),
            carousel: Math.ceil(newTotal * (contentTypeDist.carousel / 100))
        };

        setPlan(prev => ({
            ...prev,
            config: {
                ...prev.config,
                includeOrganic: newIncludeOrganic,
                includePaid: newIncludePaid
            },
            recommendations: {
                ...prev.recommendations,
                totalPosts: newTotal,
                organicPosts: newOrganicPosts,
                paidCreatives: newPaidCreatives,
                byFunnelStage: newByFunnelStage,
                byContentType: newByContentType
            }
        }));
    }, [plan]);

    // Handle manual entry of total posts (redistributes between organic/paid based on current ratio)
    const handleTotalChange = useCallback((newTotal) => {
        if (!plan) return;

        const { config, recommendations } = plan;
        const numValue = Math.max(0, parseInt(newTotal) || 0);

        // Calculate current ratio of organic to paid
        const currentTotal = recommendations.organicPosts + recommendations.paidCreatives;
        let newOrganicPosts, newPaidCreatives;

        if (config.includeOrganic && config.includePaid && currentTotal > 0) {
            // Both enabled - maintain current ratio
            const organicRatio = recommendations.organicPosts / currentTotal;
            newOrganicPosts = Math.round(numValue * organicRatio);
            newPaidCreatives = numValue - newOrganicPosts;
        } else if (config.includeOrganic && !config.includePaid) {
            // Only organic
            newOrganicPosts = numValue;
            newPaidCreatives = 0;
        } else if (!config.includeOrganic && config.includePaid) {
            // Only paid
            newOrganicPosts = 0;
            newPaidCreatives = numValue;
        } else {
            // Default to organic
            newOrganicPosts = numValue;
            newPaidCreatives = 0;
        }

        // Update base counts to reflect manual changes
        const newBaseOrganic = config.includeOrganic ? newOrganicPosts : config.baseOrganicPosts;
        const newBasePaid = config.includePaid ? newPaidCreatives : config.basePaidCreatives;

        // Recalculate distributions
        const { byFunnelStage } = recommendations;
        const contentTypeDist = config.contentTypeDistribution || { image: 50, video: 30, carousel: 20 };

        const newByFunnelStage = {
            TOFU: { count: Math.ceil(numValue * (byFunnelStage.TOFU.percentage / 100)), percentage: byFunnelStage.TOFU.percentage },
            MOFU: { count: Math.ceil(numValue * (byFunnelStage.MOFU.percentage / 100)), percentage: byFunnelStage.MOFU.percentage },
            BOFU: { count: numValue - Math.ceil(numValue * (byFunnelStage.TOFU.percentage / 100)) - Math.ceil(numValue * (byFunnelStage.MOFU.percentage / 100)), percentage: byFunnelStage.BOFU.percentage }
        };

        const newByContentType = {
            image: Math.ceil(numValue * (contentTypeDist.image / 100)),
            video: Math.ceil(numValue * (contentTypeDist.video / 100)),
            carousel: Math.ceil(numValue * (contentTypeDist.carousel / 100))
        };

        // Also update pillar distribution
        const newByPillar = {};
        const pillarEntries = Object.entries(recommendations.byPillar);
        if (pillarEntries.length > 0) {
            pillarEntries.forEach(([pillarId, pillarData]) => {
                const newCount = Math.round(numValue * (pillarData.percentage / 100));
                newByPillar[pillarId] = {
                    ...pillarData,
                    recommended: newCount
                };
            });
        }

        setPlan(prev => ({
            ...prev,
            config: {
                ...prev.config,
                baseOrganicPosts: newBaseOrganic,
                basePaidCreatives: newBasePaid
            },
            recommendations: {
                ...prev.recommendations,
                totalPosts: numValue,
                organicPosts: newOrganicPosts,
                paidCreatives: newPaidCreatives,
                byFunnelStage: newByFunnelStage,
                byContentType: newByContentType,
                byPillar: Object.keys(newByPillar).length > 0 ? newByPillar : prev.recommendations.byPillar
            }
        }));
    }, [plan]);

    // Handle manual entry of organic/paid counts
    const handlePostCountChange = useCallback((type, value) => {
        if (!plan) return;

        const { config, recommendations } = plan;
        const numValue = Math.max(0, parseInt(value) || 0);

        let newOrganicPosts = recommendations.organicPosts;
        let newPaidCreatives = recommendations.paidCreatives;

        if (type === 'organic') {
            newOrganicPosts = numValue;
        } else if (type === 'paid') {
            newPaidCreatives = numValue;
        }

        const newTotal = newOrganicPosts + newPaidCreatives;

        // Update base counts to reflect manual changes
        const newBaseOrganic = config.includeOrganic ? newOrganicPosts : config.baseOrganicPosts;
        const newBasePaid = config.includePaid ? newPaidCreatives : config.basePaidCreatives;

        // Recalculate distributions
        const { byFunnelStage } = recommendations;
        const contentTypeDist = config.contentTypeDistribution || { image: 50, video: 30, carousel: 20 };

        const newByFunnelStage = {
            TOFU: { count: Math.ceil(newTotal * (byFunnelStage.TOFU.percentage / 100)), percentage: byFunnelStage.TOFU.percentage },
            MOFU: { count: Math.ceil(newTotal * (byFunnelStage.MOFU.percentage / 100)), percentage: byFunnelStage.MOFU.percentage },
            BOFU: { count: newTotal - Math.ceil(newTotal * (byFunnelStage.TOFU.percentage / 100)) - Math.ceil(newTotal * (byFunnelStage.MOFU.percentage / 100)), percentage: byFunnelStage.BOFU.percentage }
        };

        const newByContentType = {
            image: Math.ceil(newTotal * (contentTypeDist.image / 100)),
            video: Math.ceil(newTotal * (contentTypeDist.video / 100)),
            carousel: Math.ceil(newTotal * (contentTypeDist.carousel / 100))
        };

        // Also update pillar distribution
        const newByPillar = {};
        const pillarEntries = Object.entries(recommendations.byPillar);
        if (pillarEntries.length > 0) {
            pillarEntries.forEach(([pillarId, pillarData]) => {
                const newCount = Math.round(newTotal * (pillarData.percentage / 100));
                newByPillar[pillarId] = {
                    ...pillarData,
                    recommended: newCount
                };
            });
        }

        setPlan(prev => ({
            ...prev,
            config: {
                ...prev.config,
                baseOrganicPosts: newBaseOrganic,
                basePaidCreatives: newBasePaid
            },
            recommendations: {
                ...prev.recommendations,
                totalPosts: newTotal,
                organicPosts: newOrganicPosts,
                paidCreatives: newPaidCreatives,
                byFunnelStage: newByFunnelStage,
                byContentType: newByContentType,
                byPillar: Object.keys(newByPillar).length > 0 ? newByPillar : prev.recommendations.byPillar
            }
        }));
    }, [plan]);

    // Handle date range changes
    const handleDateRangeChange = useCallback(async (newStartDate, newEndDate) => {
        if (!plan) return;

        // Calculate days in range
        const start = new Date(newStartDate);
        const end = new Date(newEndDate);
        const daysInRange = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;

        // Recalculate recommendations with new date range
        const newConfig = { ...plan.config, daysInRange };
        const newRecommendations = await calculateRecommendedVolume(
            selectedBrand.id,
            newConfig,
            pillars,
            businessGoals
        );

        // Update base counts for the new date range
        const updatedConfig = {
            ...newConfig,
            baseOrganicPosts: newRecommendations.organicPosts,
            basePaidCreatives: newRecommendations.paidCreatives
        };

        setPlan(prev => ({
            ...prev,
            startDate: newStartDate,
            endDate: newEndDate,
            config: updatedConfig,
            recommendations: newRecommendations
        }));

        // Clear events when date range changes
        setSelectedEvents([]);
    }, [plan, selectedBrand?.id, pillars, businessGoals]);

    // Handle A/B test configuration
    const handleConfigureTest = async (suggestion) => {
        if (!suggestion || !suggestion.briefs || suggestion.briefs.length < 1) {
            setError('Invalid test configuration: no briefs associated with this test');
            return;
        }

        setConfiguringTest(suggestion);
        setTestConfigProgress({ phase: 'creating_variant', current: 0, total: 1 });

        try {
            // Get the CURRENT version of the brief from state, not the stale snapshot from suggestions
            // This ensures we preserve any refinements the user made
            const baseBrief = briefs.find(b => b.id === suggestion.briefs[0].id) || suggestion.briefs[0];
            const testElement = suggestion.testElement || 'headline';

            // Generate A/B variant
            const result = await generateABVariant(baseBrief.brief || baseBrief, testElement);

            if (result?.variant) {
                // Update the base brief to mark it as part of an A/B test (auto-approved)
                const updatedBaseBrief = {
                    ...baseBrief,
                    status: 'approved',
                    abTest: {
                        isAbTest: true,
                        testElement,
                        isVariantA: true,
                        testId: `test_${Date.now()}`,
                        hypothesis: result.hypothesis || suggestion.hypothesis
                    }
                };

                // Create variant brief (auto-approved since it was generated from a configured test)
                const variantBrief = {
                    ...baseBrief,
                    id: `${baseBrief.id}_variant`,
                    brief: result.variant,
                    status: 'approved',
                    abTest: {
                        isAbTest: true,
                        testElement,
                        isVariantB: true,
                        testId: updatedBaseBrief.abTest.testId,
                        hypothesis: result.hypothesis || suggestion.hypothesis
                    }
                };

                // Update briefs state
                setBriefs(prev => {
                    const updated = prev.map(b =>
                        b.id === baseBrief.id ? updatedBaseBrief : b
                    );
                    return [...updated, variantBrief];
                });

                // Update in Firestore
                await updateContentBrief(baseBrief.id, { abTest: updatedBaseBrief.abTest, status: 'approved' });

                setTestConfigProgress({ phase: 'complete' });
            }
        } catch (err) {
            console.error('Error configuring A/B test:', err);
            setError('Failed to configure A/B test: ' + err.message);
        } finally {
            setTimeout(() => {
                setConfiguringTest(null);
                setTestConfigProgress(null);
            }, 1500);
        }
    };

    // Generate briefs
    const handleGenerateBriefs = async () => {
        if (!plan || pillars.length === 0) {
            setError('Please set up your marketing pillars first');
            return;
        }

        if (!currentUser?.uid) {
            setError('You must be logged in to generate content');
            return;
        }

        if (!selectedBrand?.id) {
            setError('Please select a brand first');
            return;
        }

        setGenerating(true);
        setGenerationProgress({ phase: 'starting', current: 0, total: pillars.length });
        setError(null);

        try {
            // Include events in the plan
            const planWithEvents = {
                ...plan,
                selectedEvents: selectedEvents
            };

            // Save plan to Firestore
            const savedPlan = await createContentPlan(
                selectedBrand.id,
                currentUser.uid,
                planWithEvents
            );

            setPlan(prev => ({ ...prev, id: savedPlan.id }));
            planIdRef.current = savedPlan.id;

            // Generate briefs (including event-themed briefs and promotional briefs)
            // Filter to only active products for brief generation
            const activeProducts = products.filter(p => p.isActive);
            const generatedBriefs = await generateBulkBriefs(
                planWithEvents,
                pillars,
                selectedBrand,
                setGenerationProgress,
                selectedEvents,
                selectedPromotions,
                activeProducts,
                productAssignments // Pass product assignments for per-brief product linking
            );

            // Save briefs to Firestore
            const savedBriefs = await createContentBriefsBatch(
                savedPlan.id,
                selectedBrand.id,
                currentUser.uid,
                generatedBriefs
            );

            setBriefs(savedBriefs);
            setCompletedSteps(prev => [...prev, 'overview', 'products', 'generate']);
            setCurrentStep(3); // Move to review step

        } catch (err) {
            console.error('Error generating briefs:', err);
            setError(err.message || 'Failed to generate briefs');
        } finally {
            setGenerating(false);
            setGenerationProgress(null);
        }
    };

    // Handle brief updates
    const handleBriefUpdate = (updatedBrief) => {
        setBriefs(prev =>
            prev.map(b => b.id === updatedBrief.id ? updatedBrief : b)
        );
        // Also update selectedBrief so the modal reflects the changes immediately
        if (selectedBrief?.id === updatedBrief.id) {
            setSelectedBrief(updatedBrief);
        }
    };

    // Handle brief approval
    const handleBriefApprove = async (briefId) => {
        setBriefs(prev =>
            prev.map(b => b.id === briefId ? { ...b, status: 'approved' } : b)
        );
        // Also update selectedBrief if it's the one being approved
        if (selectedBrief?.id === briefId) {
            setSelectedBrief(prev => ({ ...prev, status: 'approved' }));
        }

        // Persist to Firestore
        try {
            await updateContentBrief(briefId, { status: 'approved' });
        } catch (error) {
            console.error('Failed to approve brief:', error);
        }
    };

    // Handle brief unapproval (toggle back to previous status)
    const handleBriefUnapprove = async (briefId) => {
        const brief = briefs.find(b => b.id === briefId);
        const newStatus = brief?.isUserRefined ? 'refined' : 'ai_generated';

        setBriefs(prev =>
            prev.map(b => b.id === briefId ? { ...b, status: newStatus } : b)
        );
        // Also update selectedBrief if it's the one being unapproved
        if (selectedBrief?.id === briefId) {
            setSelectedBrief(prev => ({ ...prev, status: newStatus }));
        }

        // Persist to Firestore
        try {
            await updateContentBrief(briefId, { status: newStatus });
        } catch (error) {
            console.error('Failed to unapprove brief:', error);
        }
    };

    // Navigate steps
    const goToStep = (stepIndex) => {
        if (stepIndex <= currentStep || completedSteps.includes(STEPS[stepIndex].id)) {
            setCurrentStep(stepIndex);
        }
    };

    const nextStep = () => {
        if (currentStep < STEPS.length - 1) {
            setCompletedSteps(prev => [...new Set([...prev, STEPS[currentStep].id])]);
            setCurrentStep(prev => prev + 1);
        }
    };

    const prevStep = () => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        }
    };

    // Handle completion
    const handleComplete = () => {
        if (onComplete) {
            onComplete({
                plan,
                briefs: briefs.filter(b => b.status === 'approved'),
                abTests: abTestSuggestions
            });
        }
        onClose();
    };

    // Reset state when closed
    useEffect(() => {
        if (!isOpen) {
            setCurrentStep(0);
            setCompletedSteps([]);
            setBriefs([]);
            setAbTestSuggestions([]);
            setSelectedEvents([]);
            setSelectedPromotions([]);
            setError(null);
            setConfiguringTest(null);
            setTestConfigProgress(null);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const currentStepData = STEPS[currentStep];
    const approvedCount = briefs.filter(b => b.status === 'approved').length;

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.4)',
                zIndex: 9999
            }}
            onClick={(e) => e.target === e.currentTarget && !isGenerationActive && onClose()}
        >
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: '820px',
                    maxWidth: '90vw',
                    backgroundColor: 'var(--color-bg-primary)',
                    borderLeft: '1px solid var(--color-border)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
                    animation: 'slideInRight 0.3s ease'
                }}
            >
                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--space-4) var(--space-6)',
                    borderBottom: '1px solid var(--color-border)'
                }}>
                    <div>
                        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <Calendar size={24} />
                            Bulk Content Planner
                        </h2>
                        <p style={{ margin: 'var(--space-1) 0 0', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                            Plan and generate a month of content in one session
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="btn btn-ghost"
                        style={{ padding: 'var(--space-2)', opacity: isGenerationActive ? 0.5 : 1 }}
                        disabled={isGenerationActive}
                        title={isGenerationActive ? 'Cannot close while generation is in progress' : 'Close'}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Step Indicator */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-3) var(--space-6)',
                    background: 'var(--color-bg-secondary)',
                    borderBottom: '1px solid var(--color-border)',
                    overflowX: 'auto'
                }}>
                    {STEPS.map((step, index) => {
                        const isActive = index === currentStep;
                        const isCompleted = completedSteps.includes(step.id);
                        const Icon = step.icon;

                        return (
                            <React.Fragment key={step.id}>
                                <button
                                    onClick={() => goToStep(index)}
                                    disabled={index > currentStep && !isCompleted}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-2)',
                                        padding: 'var(--space-2) var(--space-3)',
                                        background: isActive ? 'var(--color-primary)' : isCompleted ? 'var(--success-100)' : 'transparent',
                                        color: isActive ? 'white' : isCompleted ? 'var(--success-700)' : 'var(--color-text-secondary)',
                                        border: 'none',
                                        borderRadius: 'var(--radius-md)',
                                        cursor: index <= currentStep || isCompleted ? 'pointer' : 'not-allowed',
                                        opacity: index > currentStep && !isCompleted ? 0.5 : 1,
                                        whiteSpace: 'nowrap',
                                        fontSize: 'var(--font-size-sm)',
                                        fontWeight: isActive ? 600 : 400
                                    }}
                                >
                                    {isCompleted && !isActive ? (
                                        <CheckCircle size={16} />
                                    ) : (
                                        <Icon size={16} />
                                    )}
                                    {step.label}
                                </button>
                                {index < STEPS.length - 1 && (
                                    <ArrowRight size={14} style={{ color: 'var(--color-text-tertiary)' }} />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>

                {/* Error Banner */}
                {error && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        padding: 'var(--space-3) var(--space-6)',
                        background: 'var(--error-50)',
                        color: 'var(--error-700)',
                        borderBottom: '1px solid var(--error-200)'
                    }}>
                        <AlertCircle size={16} />
                        {error}
                        <button
                            onClick={() => setError(null)}
                            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}
                        >
                            <X size={14} />
                        </button>
                    </div>
                )}

                {/* Content */}
                <div style={{
                    flex: 1,
                    overflow: 'auto',
                    padding: 'var(--space-6)'
                }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: 'var(--space-12)' }}>
                            <Loader2 size={32} className="spin" style={{ color: 'var(--color-primary)' }} />
                            <p style={{ marginTop: 'var(--space-4)', color: 'var(--color-text-secondary)' }}>
                                Loading strategy data...
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Overview Step */}
                            {currentStep === 0 && (
                                <PlanOverview
                                    plan={plan}
                                    pillars={pillars}
                                    onPlanUpdate={handlePlanUpdate}
                                    onFunnelDistributionChange={handleFunnelDistributionChange}
                                    onContentTypeDistributionChange={handleContentTypeDistributionChange}
                                    onContentTypeToggle={handleContentTypeToggle}
                                    onPostCountChange={handlePostCountChange}
                                    onTotalChange={handleTotalChange}
                                    selectedEvents={selectedEvents}
                                    onEventsChange={setSelectedEvents}
                                    onDateRangeChange={handleDateRangeChange}
                                    promotions={promotions}
                                    selectedPromotions={selectedPromotions}
                                    onPromotionsChange={setSelectedPromotions}
                                />
                            )}

                            {/* Product Assignment Step */}
                            {currentStep === 1 && (
                                <ProductAssignmentStep
                                    plan={plan}
                                    pillars={pillars}
                                    products={products}
                                    assignments={productAssignments}
                                    onAssignmentsChange={setProductAssignments}
                                    onNext={nextStep}
                                    onBack={prevStep}
                                />
                            )}

                            {/* Generate Step */}
                            {currentStep === 2 && (
                                <div style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
                                    {generating ? (
                                        <>
                                            <div style={{
                                                width: '80px',
                                                height: '80px',
                                                margin: '0 auto var(--space-4)',
                                                borderRadius: 'var(--radius-full)',
                                                background: 'var(--color-bg-secondary)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <Loader2 size={40} className="spin" style={{ color: 'var(--color-primary)' }} />
                                            </div>
                                            <h3>Generating Content Briefs</h3>
                                            {generationProgress && (
                                                <p style={{ color: 'var(--color-text-secondary)' }}>
                                                    {generationProgress.phase === 'generating' && (
                                                        <>Processing pillar: {generationProgress.pillar} ({generationProgress.current + 1}/{generationProgress.total})</>
                                                    )}
                                                    {generationProgress.phase === 'waiting' && (
                                                        <>{generationProgress.message || 'Pacing requests...'} ({generationProgress.current}/{generationProgress.total})</>
                                                    )}
                                                    {generationProgress.phase === 'complete' && (
                                                        <>Generated {generationProgress.total} briefs!</>
                                                    )}
                                                </p>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <div style={{
                                                width: '80px',
                                                height: '80px',
                                                margin: '0 auto var(--space-4)',
                                                borderRadius: 'var(--radius-full)',
                                                background: 'var(--color-bg-secondary)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <Sparkles size={40} style={{ color: 'var(--color-primary)' }} />
                                            </div>
                                            <h3>Ready to Generate</h3>
                                            <p style={{ color: 'var(--color-text-secondary)', maxWidth: '500px', margin: '0 auto var(--space-6)' }}>
                                                AI will generate {plan?.recommendations?.totalPosts || 0} content briefs based on your pillars and funnel distribution.
                                                You can refine them in the next step.
                                            </p>
                                            <button
                                                onClick={handleGenerateBriefs}
                                                className="btn btn-primary btn-lg"
                                                disabled={pillars.length === 0}
                                            >
                                                <Sparkles size={18} />
                                                Generate {plan?.recommendations?.totalPosts || 0} Briefs
                                            </button>
                                            {pillars.length === 0 && (
                                                <p style={{ color: 'var(--error-600)', marginTop: 'var(--space-3)', fontSize: 'var(--font-size-sm)' }}>
                                                    Please create at least one marketing pillar first
                                                </p>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Review Step */}
                            {currentStep === 3 && (
                                <BriefGrid
                                    briefs={briefs}
                                    onBriefClick={(brief) => {
                                        setSelectedBrief(brief);
                                        setShowBriefDetail(true);
                                    }}
                                    onBriefApprove={handleBriefApprove}
                                    onBriefUnapprove={handleBriefUnapprove}
                                />
                            )}

                            {/* Generate Creatives Step */}
                            {currentStep === 4 && (
                                <BulkGenerationStep
                                    briefs={briefs}
                                    setBriefs={setBriefs}
                                    brand={selectedBrand}
                                    planId={plan?.id || planIdRef.current}
                                    products={products}
                                    targetPlatforms={plan?.config?.targetPlatforms || ['instagram', 'facebook']}
                                    onGenerationStateChange={setIsGenerationActive}
                                    onComplete={(results) => {
                                        console.log('[BulkContentPlanner] onComplete received, results:', results);
                                        console.log('[BulkContentPlanner] Current step before:', currentStep);
                                        // Only keep briefs that were approved (have a matching result)
                                        const approvedBriefIds = new Set(results.map(r => r.briefId));
                                        const updatedBriefs = briefs
                                            .filter(brief => approvedBriefIds.has(brief.id))
                                            .map(brief => {
                                                const result = results.find(r => r.briefId === brief.id);
                                                return {
                                                    ...brief,
                                                    status: 'generated',
                                                    generatedCreativeId: result.libraryItemId,
                                                    generatedImageUrl: result.imageUrl,
                                                    generatedThumbnailUrl: result.thumbnailUrl || result.imageUrl,
                                                    // Include carousel images for carousel-type content
                                                    ...(result.carouselImages && { carouselImages: result.carouselImages })
                                                };
                                            });
                                        setBriefs(updatedBriefs);
                                        setCompletedSteps(prev => [...new Set([...prev, 'creatives'])]);

                                        // Generate A/B test suggestions from the approved creatives
                                        suggestABTests(updatedBriefs).then(suggestions => {
                                            setAbTestSuggestions(suggestions?.suggestions || []);
                                        }).catch(err => {
                                            console.warn('A/B test suggestions failed (non-critical):', err.message);
                                        });

                                        console.log('[BulkContentPlanner] Setting currentStep to 5 (A/B testing)');
                                        setCurrentStep(5); // Move to A/B testing
                                    }}
                                    onBack={prevStep}
                                />
                            )}

                            {/* A/B Testing Step */}
                            {currentStep === 5 && (
                                <div>
                                    {configuringTest && (
                                        <div style={{
                                            position: 'fixed',
                                            inset: 0,
                                            background: 'rgba(0,0,0,0.5)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            zIndex: 10001
                                        }}>
                                            <div className="card" style={{
                                                padding: 'var(--space-6)',
                                                textAlign: 'center',
                                                maxWidth: '400px'
                                            }}>
                                                {testConfigProgress?.phase === 'complete' ? (
                                                    <>
                                                        <CheckCircle size={48} style={{ color: 'var(--success-500)', marginBottom: 'var(--space-3)' }} />
                                                        <h4>A/B Test Configured!</h4>
                                                        <p style={{ color: 'var(--color-text-secondary)' }}>
                                                            Variant created successfully.
                                                        </p>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Loader2 size={48} className="spin" style={{ color: 'var(--color-primary)', marginBottom: 'var(--space-3)' }} />
                                                        <h4>Creating A/B Variant...</h4>
                                                        <p style={{ color: 'var(--color-text-secondary)' }}>
                                                            Generating {configuringTest.testElement} variation
                                                        </p>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    <ABTestSuggestions
                                        suggestions={abTestSuggestions}
                                        briefs={briefs}
                                        onConfigureTest={handleConfigureTest}
                                    />
                                </div>
                            )}

                            {/* Smart Schedule Step */}
                            {currentStep === 6 && (
                                <SmartScheduleStep
                                    briefs={briefs}
                                    startDate={plan?.startDate}
                                    endDate={plan?.endDate}
                                    platforms={plan?.config?.targetPlatforms || ['instagram', 'facebook']}
                                    onScheduledPostsChange={setScheduledPosts}
                                    onSchedule={async (scheduledPosts) => {
                                        try {
                                            // Save scheduled posts to Firestore with latest brief content
                                            // This ensures any refinements made during the session are captured
                                            // Also creates posts in posts collection for organic content (Phase 1)
                                            await scheduleContentBriefs(
                                                plan?.id || planIdRef.current,
                                                selectedBrand?.id,
                                                currentUser?.uid,
                                                scheduledPosts,
                                                briefs,  // Pass full briefs to persist latest revisions
                                                selectedBrand  // Pass brand for organic post scheduling
                                            );

                                            // Update local briefs with scheduled status
                                            const updatedBriefs = briefs.map(brief => {
                                                const scheduled = scheduledPosts.find(p => p.briefId === brief.id);
                                                if (scheduled) {
                                                    return {
                                                        ...brief,
                                                        status: 'scheduled',
                                                        scheduledDate: scheduled.dateTime,
                                                        scheduledTime: scheduled.time
                                                    };
                                                }
                                                return brief;
                                            });
                                            setBriefs(updatedBriefs);
                                            setCompletedSteps(prev => [...new Set([...prev, 'schedule'])]);

                                            // Complete the workflow
                                            onComplete?.({
                                                plan,
                                                briefs: updatedBriefs,
                                                scheduledPosts,
                                                abTests: abTestSuggestions
                                            });
                                            onClose();
                                        } catch (error) {
                                            console.error('Failed to schedule posts:', error);
                                            setError('Failed to schedule posts: ' + error.message);
                                        }
                                    }}
                                    onBack={prevStep}
                                    onDeployPaid={() => setShowDeployToFunnel(true)}
                                />
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--space-4) var(--space-6)',
                    borderTop: '1px solid var(--color-border)',
                    background: 'var(--color-bg-secondary)'
                }}>
                    <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                        {briefs.length > 0 && (
                            <>{approvedCount} of {briefs.length} briefs approved</>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                        {/* Back button - show on steps 0, 3, 5 (others have their own back) */}
                        {(currentStep === 0 || currentStep === 3 || currentStep === 5) && currentStep > 0 && (
                            <button onClick={prevStep} className="btn btn-ghost">
                                <ArrowLeft size={16} />
                                Back
                            </button>
                        )}
                        {/* Forward buttons */}
                        {currentStep === 0 && (
                            <button onClick={nextStep} className="btn btn-primary">
                                Continue
                                <ArrowRight size={16} />
                            </button>
                        )}
                        {currentStep === 3 && (
                            <button
                                onClick={nextStep}
                                className="btn btn-primary"
                                disabled={approvedCount === 0}
                            >
                                Generate Creatives
                                <ArrowRight size={16} />
                            </button>
                        )}
                        {currentStep === 5 && (
                            <button
                                onClick={() => {
                                    setCompletedSteps(prev => [...new Set([...prev, 'testing'])]);
                                    setCurrentStep(6);
                                }}
                                className="btn btn-primary"
                            >
                                Continue to Schedule
                                <ArrowRight size={16} />
                            </button>
                        )}
                        {/* Steps 4 and 6 have their own navigation in their components */}
                        {/* Deploy to Funnel - available on step 6 (schedule) when paid creatives exist */}
                        {currentStep === 6 && briefs.some(b => b.contentType === 'paid' && b.generatedCreativeId) && (
                            <button
                                onClick={() => setShowDeployToFunnel(true)}
                                className="btn btn-secondary"
                            >
                                <Layers size={16} />
                                Deploy to Funnel
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Deploy to Funnel Modal */}
            <DeployToFunnelModal
                isOpen={showDeployToFunnel}
                onClose={() => setShowDeployToFunnel(false)}
                briefs={briefs}
                scheduledPosts={scheduledPosts}
                onComplete={(template, platform, pillarMapping) => {
                    navigate('/funnel-builder', {
                        state: { generatedTemplate: template, platform, pillarMapping }
                    });
                    onClose();
                }}
            />

            {/* Brief Detail Modal */}
            {showBriefDetail && selectedBrief && (
                <BriefDetailModal
                    brief={selectedBrief}
                    onClose={() => {
                        setShowBriefDetail(false);
                        setSelectedBrief(null);
                    }}
                    onUpdate={handleBriefUpdate}
                    onApprove={() => {
                        handleBriefApprove(selectedBrief.id);
                        setShowBriefDetail(false);
                        setSelectedBrief(null);
                    }}
                    onUnapprove={() => {
                        handleBriefUnapprove(selectedBrief.id);
                    }}
                />
            )}
        </div>
    );
};

// Utility function
function getMonthName(monthIndex) {
    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[monthIndex] || 'Unknown';
}

export default BulkContentPlanner;
